/**
 * @fileoverview Local-first system-web service for tenant-scoped import-run upload, dry-run, polling, commit, cancel, and evidence access.
 * @module system-web/import-run-service
 *
 * Responsibilities:
 * - Serve import-run reads from Dexie first and refresh them from the system-api proxy when needed.
 * - Execute online import mutations such as upload, parse, dry-run creation, commit, and cancel.
 * - Write lifecycle run snapshots back into Dexie so system-web remains local-first for subsequent reads.
 *
 * Dependencies:
 * - `getSystemDb` from `@firesquirrel/local-db`
 * - `pollGeoConversionJob` / `isAsyncGeoSubmission` from `@firesquirrel/import-editor`
 * - `apiFetch`, `apiPostFormData`, and `getToken` from `../apiClient`
 * - `telemetry` from `./telemetry`
 * - System import-run proxy routes exposed by system-api
 *
 * Architecture:
 * - Local-first reads: Dexie is the UI source of truth for run history and cached run detail.
 * - Online-only writes: upload, parse, dry-run, commit, and cancel are network operations that sync results back into Dexie.
 * - In-progress parse and dry-run state is persisted on `import_runs`; there is no separate preview-session cache.
 * - Geo uploads and geo parses are converted by the importProcessing worker, so those surfaces poll a
 *   server-authoritative `backgroundJobs` document instead of receiving an inline result.
 * - No component should call the import-run proxy endpoints directly; they must go through this service.
 *
 * Notes:
 * - Gate 3 handoff uses the same run-creation path as manual run creation once the template has been published.
 * - Polling intentionally bypasses Dexie on each interval so the UI sees live server progress, then writes terminal state through.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { isAsyncGeoSubmission, pollGeoConversionJob } from '@firesquirrel/import-editor';
import type { ImportRun, FileLayerInfo, ImportRunParseResult, FieldMapping, FieldTransformEvidence } from '@firesquirrel/local-db';
import type {
  AsyncGeoSubmission,
  GeoConversionJobResponse,
  GeoUploadConversionResult,
} from '@firesquirrel/import-editor';
import { apiFetch, apiPostFormData, getToken } from '../apiClient';
import { writeAuditLog } from './systemAuditLogService';
import { telemetry } from './telemetry';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);
const ACTIVE_STATUSES = new Set(['draft', 'uploading', 'queued', 'parsing', 'parsed', 'dry-run', 'awaiting-commit', 'committing']);

/**
 * @contract ParseResult
 * Purpose: Parser preview metadata returned by the Blob-backed preview flow.
 * Fields: fileType, columns, sampleRows, totalRows, hasGeometry, geometryColumn?,
 *   layers?, activeLayer?, availableSheets?, activeSheet?.
 * Rules: hasGeometry indicates whether the source has spatial data.
 * Sync: Returned from online preview actions; persisted on import_runs for Dexie projection.
 */
export interface ParseResult {
  fileType: string;
  columns: string[];
  sampleRows: Record<string, unknown>[];
  totalRows: number;
  hasGeometry: boolean;
  geometryColumn?: string;
  layers?: FileLayerInfo[];
  activeLayer?: string;
  availableSheets?: string[];
  activeSheet?: string;
}

/**
 * @contract UploadResult
 * Purpose: Result of uploading a file to Blob Storage via the system-api proxy.
 * Fields: runId, filename, fileSize, fileType, blobUrl.
 * Rules: blobUrl may be null if the upload endpoint did not return one.
 * Sync: Returned from the online upload endpoint; used to create an ImportRun.
 */
export interface UploadResult {
  runId: string;
  filename: string;
  fileSize: number;
  fileType: string;
  blobUrl: string | null;
}

/**
 * @contract ConversionJobResponse
 * Purpose: System-web alias for the shared worker geo conversion/parse polling snapshot.
 * Fields: jobId, jobType, status, progress, message, result, error.
 * Rules: result is populated only for completed jobs; error is populated for terminal failures.
 * Sync: Read from the server-authoritative backgroundJobs document through explicit online polling.
 */
export type ConversionJobResponse<TResult = Record<string, unknown>> = GeoConversionJobResponse<TResult>;

const CONVERSION_TELEMETRY_EVENTS = {
  started: 'geo_conversion_poll_started',
  completed: 'geo_conversion_poll_completed',
  failed: 'geo_conversion_poll_failed',
} as const;

// Upload progress is a single monotonic 0-100 track shared by two phases: the
// browser→Blob byte transfer, then worker conversion for geo files. Byte
// transfer owns 0-90 and conversion owns 90-100 so the indicator never moves
// backwards when a geo file hands off to the worker. Non-geo uploads finish at
// 100 as soon as confirm-upload (or the multipart fallback) returns.
const TRANSFER_PROGRESS_CEILING = 90;

function reportTransferProgress(onProgress: ((pct: number) => void) | undefined, loadedBytes: number, totalBytes: number): void {
  if (totalBytes <= 0) return;
  const fraction = Math.min(loadedBytes / totalBytes, 1);
  onProgress?.(Math.round(fraction * TRANSFER_PROGRESS_CEILING));
}

function reportConversionProgress<TResult>(
  onProgress: ((pct: number) => void) | undefined,
  job: ConversionJobResponse<TResult>,
): void {
  const percent = Math.min(Math.max(job.progress?.percent ?? 0, 0), 100);
  onProgress?.(TRANSFER_PROGRESS_CEILING + Math.round((percent * (100 - TRANSFER_PROGRESS_CEILING)) / 100));
}

/**
 * @localFirst
 * Source of Truth: Server-side backgroundJobs document (online-only conversion workflow).
 * Write Flow: GET /api/system/import-runs/conversion-jobs/:jobId until the worker reaches a terminal state; no Dexie write.
 * Sync Strategy: Explicit polling because the caller must await a blob or parse result before continuing.
 * Conflict Resolution: Worker terminal state wins.
 *
 * @process PollConversionJob
 * Steps: Delegate to the shared `pollGeoConversionJob` loop with the system conversion-job reader and system telemetry sink.
 * Inputs: Job id plus optional interval, timeout, stale threshold, and progress callback.
 * Outputs: Completed ConversionJobResponse whose result payload is present.
 * Failure Handling: Surfaces sanitized worker errors and bounded cancellation/stale/timeout errors to the wizard.
 * Idempotency: REQUIRED — GET-only polling has no writes.
 *
 * @errorHandling
 * Strategy: Owned by the shared loop — fail on worker failure/cancellation, missing terminal payload, stale progress, or timeout.
 * User Impact: The wizard displays a retryable conversion error instead of waiting indefinitely.
 * Logging: Emits sanitized lifecycle telemetry without file contents, blob URLs, or SAS credentials.
 *
 * @logging
 * Events: geo_conversion_poll_started, geo_conversion_poll_completed, geo_conversion_poll_failed.
 * Level: info and error-equivalent domain events.
 * Correlation: background job id and job type only.
 */
export async function pollConversionJob<TResult>(
  jobId: string,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    staleMs?: number;
    onProgress?: (job: ConversionJobResponse<TResult>) => void;
  } = {},
): Promise<ConversionJobResponse<TResult> & { result: TResult }> {
  return pollGeoConversionJob<TResult>({
    jobId,
    ...options,
    fetchJob: id => apiFetch(
      `/api/system/import-runs/conversion-jobs/${encodeURIComponent(id)}`,
      {},
    ) as Promise<ConversionJobResponse<TResult>>,
    onEvent: (event, properties) => telemetry.trackEvent(CONVERSION_TELEMETRY_EVENTS[event], properties),
  });
}

/**
 * @process ResolveUploadResponse
 * Steps: Return a synchronous non-geo upload result unchanged, otherwise poll the worker job and project its payload into UploadResult.
 * Inputs: The confirm-upload/multipart response, the reserved run id, and the caller's progress callback.
 * Outputs: A resolved UploadResult carrying a converted GeoParquet blob URL for geo sources.
 * Failure Handling: Propagates worker failure, cancellation, stale, and timeout errors from the shared polling loop.
 * Idempotency: REQUIRED — reads job state only.
 *
 * @errorHandling
 * Strategy: Reject async submissions that carry no usable run id rather than creating an unlinked import run.
 * User Impact: The wizard stays on the upload step and shows a retryable error.
 * Logging: Delegated to pollConversionJob; no blob URL or SAS token is logged.
 */
async function resolveUploadResponse(
  response: UploadResult | AsyncGeoSubmission,
  reservedRunId: string,
  onProgress?: (pct: number) => void,
): Promise<UploadResult> {
  if (!isAsyncGeoSubmission(response)) {
    onProgress?.(100);
    return response;
  }
  const runId = response.runId ?? reservedRunId;
  if (!runId) throw new Error('Geo conversion was accepted without an upload run id. Please try again.');
  const completed = await pollConversionJob<GeoUploadConversionResult>(response.jobId, {
    onProgress: job => reportConversionProgress(onProgress, job),
  });
  onProgress?.(100);
  return {
    runId,
    filename: completed.result.filename,
    fileSize: completed.result.sizeBytes,
    fileType: completed.result.fileType,
    blobUrl: completed.result.blobUrl,
  };
}

/**
 * @process ResolveParseResponse
 * Steps: Return a synchronous non-geo parse result unchanged, otherwise poll the worker job and return its sampled payload.
 * Inputs: The parse response and an optional worker job progress callback.
 * Outputs: The parser preview metadata the mapping UI consumes.
 * Failure Handling: Propagates worker failure, cancellation, stale, and timeout errors from the shared polling loop.
 * Idempotency: REQUIRED — reads job state only.
 */
async function resolveParseResponse(
  response: ParseResult | AsyncGeoSubmission,
  onProgress?: (job: ConversionJobResponse<ParseResult>) => void,
): Promise<ParseResult> {
  if (!isAsyncGeoSubmission(response)) return response;
  const completed = await pollConversionJob<ParseResult>(response.jobId, { onProgress });
  return completed.result;
}

/**
 * @contract CreateDraftImportRunInput
 *
 * Purpose:
 * - Seed a local-first import run record before upload/preview/dry-run so
 *   every attempt exists in import_runs from the moment the wizard starts.
 *
 * Fields:
 * - tenantId: selected tenant for the import.
 * - templateId/importName/importDescription: optional draft metadata.
 * - filename/fileSize/fileType: optional starting file metadata.
 * - selectedSheet/fieldMappings/importBehavior/boundaryCheckEnabled: current wizard draft state.
 *
 * Rules:
 * - Produces a client-generated ObjectId-like `_id` so the same run can be
 *   reused by upload, preview, dry-run, and delete paths.
 *
 * Sync:
 * - Written to Dexie first as a provisional record that generic sync must not push before tenant-api creates the authoritative run.
 */
export interface CreateDraftImportRunInput {
  tenantId: string;
  templateId?: string;
  importName?: string;
  importDescription?: string;
  filename?: string;
  fileSize?: number;
  fileType?: string;
  blobUrl?: string | null;
  selectedSheet?: string | null;
  fieldMappings?: FieldMapping[];
  importBehavior?: ImportRun['importBehavior'];
  boundaryCheckEnabled?: boolean;
  parseResult?: ImportRunParseResult | null;
}

/**
 * @contract UpdateDraftImportRunInput
 * Purpose: Patch shape for updating a provisional draft import run in Dexie.
 * Fields: templateId?, importName?, importDescription?, filename?, fileSize?,
 *   fileType?, blobUrl?, status?, previewJobId?, parseResult?, selectedSheet?,
 *   fieldMappings?, importBehavior?, matchKeyField?, boundaryCheckEnabled?,
 *   summary?, errorMessage?, columnCount?, isDryRun?, completedAt?.
 * Rules: All fields optional; null values for blobUrl/previewJobId/parseResult/
 *   errorMessage clear those fields. Produces an updated ImportRun in Dexie.
 * Sync: Written to Dexie first as a provisional record; sync must not push before
 *   tenant-api creates the authoritative run.
 */
export interface UpdateDraftImportRunInput {
  templateId?: string;
  importName?: string;
  importDescription?: string;
  filename?: string;
  fileSize?: number;
  fileType?: string;
  blobUrl?: string | null;
  status?: ImportRun['status'];
  previewJobId?: string | null;
  parseResult?: ImportRunParseResult | null;
  selectedSheet?: string | null;
  fieldMappings?: FieldMapping[];
  importBehavior?: ImportRun['importBehavior'];
  matchKeyField?: string;
  boundaryCheckEnabled?: boolean;
  summary?: ImportRun['summary'];
  errorMessage?: string | null;
  columnCount?: number;
  isDryRun?: boolean;
  completedAt?: string;
}

function createClientRunId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(16).padStart(12, '0')}${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}`.slice(0, 24);
}

/**
 * @process createDraftImportRun
 *
 * Steps:
 * 1. Generate a stable client-side run id.
 * 2. Write the initial draft record to Dexie.
 * 3. Wake local listeners so the draft is visible immediately without making it authoritative on the server.
 *
 * Inputs:
 * - input: starting draft metadata for the import attempt.
 *
 * Outputs:
 * - The newly created draft import run.
 *
 * Failure Handling:
 * - Propagates Dexie write failures.
 *
 * Idempotency:
 * - NOT REQUIRED
 *
 * @localFirst
 * Source of Truth: Dexie `import_runs` (provisional local draft).
 * Write Flow: put to Dexie + wakeLocalWrite.
 * Sync Strategy: provisional record — sync must not push before tenant-api creates
 *   the authoritative run.
 * Conflict Resolution: server wins — server-created run replaces provisional record.
 */
export async function createDraftImportRun(input: CreateDraftImportRunInput): Promise<ImportRun> {
  const db = await getSystemDb();
  const now = new Date().toISOString();
  const run: ImportRun & { _provisionalImportRun: boolean } = {
    _id: createClientRunId(),
    tenantId: input.tenantId,
    templateId: input.templateId,
    importName: input.importName,
    importDescription: input.importDescription,
    filename: input.filename ?? '',
    fileSize: input.fileSize,
    fileType: input.fileType,
    blobUrl: input.blobUrl ?? undefined,
    status: 'draft',
    isDryRun: true,
    selectedSheet: input.selectedSheet ?? null,
    fieldMappings: input.fieldMappings ?? [],
    importBehavior: input.importBehavior as any,
    boundaryCheckEnabled: input.boundaryCheckEnabled,
    parseResult: input.parseResult ?? undefined,
    startedAt: now,
    startedBy: 'system-web',
    updatedAt: now,
    _provisionalImportRun: true,
  };
  await db.import_runs.put(run as any);
  wakeLocalWrite('import_runs');

  try {
    await writeAuditLog({
      collection: 'import_runs',
      documentId: run._id,
      action: 'create',
      summary: `Created import run draft: ${input.importName ?? input.filename ?? run._id} (tenant: ${input.tenantId})`,
    });
  } catch { /* audit failure must not block mutation */ }

  return run;
}

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` (provisional local draft).
 * Write Flow: put to Dexie + wakeLocalWrite.
 * Sync Strategy: provisional record — sync must not push before tenant-api creates
 *   the authoritative run.
 * Conflict Resolution: server wins — server-created run replaces provisional record.
 */
export async function updateDraftImportRun(id: string, patch: UpdateDraftImportRunInput): Promise<ImportRun | null> {
  const db = await getSystemDb();
  const existing = await db.import_runs.get(id) as ImportRun | null;
  if (!existing || (existing as any)._deleted) return null;
  const next: ImportRun = {
    ...existing,
    ...patch,
    blobUrl: patch.blobUrl === null ? undefined : patch.blobUrl ?? existing.blobUrl,
    previewJobId: patch.previewJobId === null ? undefined : patch.previewJobId ?? existing.previewJobId,
    parseResult: patch.parseResult === null ? undefined : patch.parseResult ?? existing.parseResult,
    errorMessage: patch.errorMessage === null ? undefined : patch.errorMessage ?? existing.errorMessage,
    updatedAt: new Date().toISOString(),
  };
  await db.import_runs.put(next as any);
  wakeLocalWrite('import_runs');
  return next;
}

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` (provisional local draft).
 * Write Flow: soft-delete put to Dexie (_deleted tombstone + deletedBy) + wakeLocalWrite.
 * Sync Strategy: provisional record — sync must not push before tenant-api creates
 *   the authoritative run.
 * Conflict Resolution: server wins — server-created run replaces provisional record.
 */
export async function deleteImportRun(id: string): Promise<boolean> {
  const db = await getSystemDb();
  const existing = await db.import_runs.get(id) as ImportRun | null;
  if (!existing || (existing as any)._deleted) return false;
  const now = new Date().toISOString();
  // G-016: deletedBy is required by the architecture.md delete() standard.
  // `import_runs` is server-authoritative; deletedBy is the local actor.
  await db.import_runs.put({ ...(existing as any), _deleted: true, deletedAt: now, deletedBy: 'system-web', updatedAt: now } as any);
  wakeLocalWrite('import_runs');

  try {
    await writeAuditLog({
      collection: 'import_runs',
      documentId: id,
      action: 'delete',
      summary: `Deleted import run: ${(existing as any).importName ?? (existing as any).filename ?? id}`,
      changes: [{ field: '_deleted', oldValue: false, newValue: true, fieldType: 'boolean' }],
    });
  } catch { /* audit failure must not block mutation */ }

  return true;
}

// ---------------------------------------------------------------------------
// Async preview jobs — /upload → /preview-jobs → poll /preview-jobs/:id
// ---------------------------------------------------------------------------

/**
 * @contract PreviewJobStatus
 * Purpose: Closed enum of async preview job lifecycle states.
 * Fields: 'queued' | 'processing' | 'succeeded' | 'failed'.
 * Rules: Terminal states are 'succeeded' and 'failed'.
 * Sync: Returned from the online preview-jobs endpoint; persisted on import_runs.
 */
export type PreviewJobStatus = 'queued' | 'processing' | 'succeeded' | 'failed';

/**
 * @contract PreviewJobResponse
 * Purpose: Response shape from the async preview-jobs polling endpoint.
 * Fields: jobId, status (PreviewJobStatus), errorMessage?, result? (ParseResult),
 *   filename?, fileSize?, fileType?, sheetName?, createdAt?, updatedAt?.
 * Rules: result is populated only when status='succeeded'; errorMessage when
 *   status='failed'.
 * Sync: Returned from the online preview-jobs endpoint; persisted on import_runs.
 */
export interface PreviewJobResponse {
  jobId: string;
  status: PreviewJobStatus;
  errorMessage?: string | null;
  result?: ParseResult | null;
  filename?: string;
  fileSize?: number;
  fileType?: string;
  sheetName?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// Upload a file directly to Azure Blob Storage via SAS URL.
// Flow: POST /initiate-upload → BlockBlobClient.uploadData → POST /confirm-upload
// Falls back to legacy multipart POST when no SAS URL is returned (dev/local).
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server (online-only exception — multi-GB file transfer).
 * Write Flow: POST initiate-upload → SAS upload → POST confirm-upload; geo confirmations poll
 *   backgroundJobs until the importProcessing worker returns GeoParquet metadata. No Dexie write.
 * Sync Strategy: Explicit online polling for geo sources; the result feeds createImportRun.
 * Conflict Resolution: Worker terminal state wins for geo conversion.
 *
 * @errorHandling
 * Strategy: Propagate transfer, confirm, and worker errors; reject async acceptance without a run id.
 * User Impact: The wizard stays on the upload step and presents a retryable error.
 * Logging: Conversion lifecycle telemetry only; SAS URLs and blob credentials are never logged.
 */
export async function uploadImportFile(
  tenantId: string,
  file: File,
  onProgress?: (pct: number) => void,
  runId?: string,
): Promise<UploadResult> {
  const initiated = await apiFetch(
    `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/initiate-upload`,
    { method: 'POST', body: JSON.stringify({ filename: file.name, fileSize: file.size, runId }) },
  ) as { runId: string; sasUrl: string | null; blobPath: string; blobUrl: string | null };

  if (initiated.sasUrl) {
    // Lazy-load the Azure Blob SDK via a standard dynamic import so Vite
    // produces a code-split chunk that is only fetched when a SAS URL is
    // returned (i.e. production uploads). The previous implementation used
    // `new Function('return import(...)')` to hide the specifier from Vite,
    // which produced a bundle the browser could not resolve at runtime
    // ("Failed to resolve module specifier '@azure/storage-blob'").
    const { BlockBlobClient } = await import('@azure/storage-blob');
    const client = new BlockBlobClient(initiated.sasUrl);
    await client.uploadData(file, {
      blockSize: 8 * 1024 * 1024,
      concurrency: 4,
      onProgress: (ev: { loadedBytes: number }) => reportTransferProgress(onProgress, ev.loadedBytes, file.size),
      blobHTTPHeaders: { blobContentType: file.type || 'application/octet-stream' },
    });
    onProgress?.(TRANSFER_PROGRESS_CEILING);

    const confirmed = await apiFetch(
      `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/confirm-upload`,
      {
        method: 'POST',
        body: JSON.stringify({
          runId: initiated.runId,
          filename: file.name,
          fileSize: file.size,
          blobPath: initiated.blobPath,
        }),
      },
    ) as UploadResult | AsyncGeoSubmission;
    return resolveUploadResponse(confirmed, initiated.runId, onProgress);
  }

  // Fallback: no Azure Storage configured (dev/local) — use legacy multipart POST
  const form = new FormData();
  form.append('file', file);
  if (runId) form.append('runId', runId);
  const uploaded = await apiPostFormData<UploadResult | AsyncGeoSubmission>(
    `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/upload`,
    form,
  );
  return resolveUploadResponse(uploaded, initiated.runId, onProgress);
}

// ---------------------------------------------------------------------------
// Parse a file and return columns + sample rows (no Blob Storage)
// Per-tenant proxy to tenant-api /api/import-runs/parse via system-api.
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server (online-only exception — file parse).
 * Write Flow: POST /api/system/import-runs/tenants/:id/parse; geo responses poll backgroundJobs
 *   until the importProcessing worker finishes sampling. No Dexie write.
 * Sync Strategy: Explicit online polling for geo sources; result feeds the mapping UI.
 * Conflict Resolution: Worker terminal state wins for geo parsing.
 *
 * @errorHandling
 * Strategy: Propagate parser and worker errors, including missing terminal payloads.
 * User Impact: The mapping UI presents a retryable parse error.
 * Logging: Conversion lifecycle telemetry only; uploaded contents are never logged.
 */
export async function parseImportFile(
  tenantId: string,
  file: File,
  sampleSize = 10,
  sheetName?: string,
  onProgress?: (job: ConversionJobResponse<ParseResult>) => void,
): Promise<ParseResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('sampleSize', String(sampleSize));
  if (sheetName) form.append('sheetName', sheetName);

  const response = await apiPostFormData<ParseResult | AsyncGeoSubmission>(
    `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/parse`,
    form,
  );
  return resolveParseResponse(response, onProgress);
}

// ---------------------------------------------------------------------------
// Tenant-agnostic variant for the template editor.
//
// Templates are reusable, system-wide definitions — authoring shouldn't
// require a tenant context. This variant hits the new
// /api/system/import-runs/parse endpoint which resolves a proxy tenant
// internally on system-api. Use this from the template editor; use the
// tenant-scoped variant above from the import run wizard where a specific
// tenant has already been selected.
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server (online-only exception — file parse, tenant-agnostic).
 * Write Flow: POST /api/system/import-runs/parse; geo responses poll backgroundJobs until the
 *   importProcessing worker finishes sampling. No Dexie write.
 * Sync Strategy: Explicit online polling for geo sources; result feeds the template editor.
 * Conflict Resolution: Worker terminal state wins for geo parsing.
 *
 * @errorHandling
 * Strategy: Propagate parser and worker errors, including missing terminal payloads.
 * User Impact: The template editor presents a retryable parse error.
 * Logging: Conversion lifecycle telemetry only; uploaded contents are never logged.
 */
export async function parseImportFileForTemplate(
  file: File,
  sampleSize = 10,
  sheetName?: string,
  onProgress?: (job: ConversionJobResponse<ParseResult>) => void,
): Promise<ParseResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('sampleSize', String(sampleSize));
  if (sheetName) form.append('sheetName', sheetName);

  const response = await apiPostFormData<ParseResult | AsyncGeoSubmission>(
    `/api/system/import-runs/parse`,
    form,
  );
  return resolveParseResponse(response, onProgress);
}

// ---------------------------------------------------------------------------
// Template-mode upload + preview-job pipeline
//
// These mirror uploadImportFile / createPreviewJob / getPreviewJob / pollPreviewJob
// but target the tenant-agnostic /api/system/import-runs/* routes which
// resolve a proxy tenant internally. Used by the authoring wizard so
// template creation goes through the exact same robust
//
//     initiate-upload → SAS upload → confirm-upload → preview-jobs
//
// pipeline that tenant-scoped imports use. This is what makes the authoring
// flow handle zipped shapefiles, multi-GB Excel, GeoPackage, etc. — the
// sync /parse endpoint cannot, and isn't meant to.
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server (online-only exception — template-mode file upload).
 * Write Flow: POST initiate-upload → SAS upload → POST confirm-upload; geo confirmations poll
 *   backgroundJobs until the importProcessing worker returns GeoParquet metadata. No Dexie write.
 * Sync Strategy: Explicit online polling for geo sources; result feeds createPreviewJobForTemplate.
 * Conflict Resolution: Worker terminal state wins for geo conversion.
 *
 * @errorHandling
 * Strategy: Propagate transfer, confirm, and worker errors; fail closed when Azure Storage is unconfigured.
 * User Impact: The template editor presents a retryable error and never advances without a blob URL.
 * Logging: Conversion lifecycle telemetry only; SAS URLs and blob credentials are never logged.
 */
export async function uploadImportFileForTemplate(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<UploadResult> {
  const initiated = await apiFetch(
    `/api/system/import-runs/initiate-upload`,
    { method: 'POST', body: JSON.stringify({ filename: file.name, fileSize: file.size }) },
  ) as { runId: string; sasUrl: string | null; blobPath: string; blobUrl: string | null };

  if (initiated.sasUrl) {
    const { BlockBlobClient } = await import('@azure/storage-blob');
    const client = new BlockBlobClient(initiated.sasUrl);
    await client.uploadData(file, {
      blockSize: 8 * 1024 * 1024,
      concurrency: 4,
      onProgress: (ev: { loadedBytes: number }) => reportTransferProgress(onProgress, ev.loadedBytes, file.size),
      blobHTTPHeaders: { blobContentType: file.type || 'application/octet-stream' },
    });
    onProgress?.(TRANSFER_PROGRESS_CEILING);

    const confirmed = await apiFetch(
      `/api/system/import-runs/confirm-upload`,
      {
        method: 'POST',
        body: JSON.stringify({
          runId: initiated.runId,
          filename: file.name,
          fileSize: file.size,
          blobPath: initiated.blobPath,
        }),
      },
    ) as UploadResult | AsyncGeoSubmission;
    return resolveUploadResponse(confirmed, initiated.runId, onProgress);
  }

  // Dev/local fallback — no SAS available. The preview-job pipeline needs a
  // blobUrl to enqueue work, and the tenant-agnostic route has no multipart
  // equivalent, so fail closed with an actionable configuration message
  // rather than advancing the wizard without a source blob.
  throw new Error(
    'Template upload requires Azure Storage configuration. ' +
      'Set AZURE_STORAGE_CONNECTION_STRING on system-api or run against a deployed environment.',
  );
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — creates an async preview job).
 * Write Flow: POST /api/system/import-runs/preview-jobs → no Dexie write.
 * Sync Strategy: n/a — job creation is inherently online; poll via getPreviewJobForTemplate.
 * Conflict Resolution: n/a.
 */
export async function createPreviewJobForTemplate(params: {
  blobUrl: string;
  filename: string;
  fileSize?: number;
  fileType?: string;
  sheetName?: string;
  sampleSize?: number;
}): Promise<{ jobId: string; status: PreviewJobStatus }> {
  return apiFetch(`/api/system/import-runs/preview-jobs`, {
    method: 'POST',
    body: JSON.stringify(params),
  }) as Promise<{ jobId: string; status: PreviewJobStatus }>;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — polls an async preview job).
 * Write Flow: GET /api/system/import-runs/preview-jobs/:id → no Dexie write.
 * Sync Strategy: n/a — polling is inherently online; caller saves via updateDraftImportRun.
 * Conflict Resolution: n/a.
 */
export async function getPreviewJobForTemplate(jobId: string): Promise<PreviewJobResponse> {
  return apiFetch(
    `/api/system/import-runs/preview-jobs/${encodeURIComponent(jobId)}`,
    {},
  ) as Promise<PreviewJobResponse>;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — polls an async preview job).
 * Write Flow: polls API via getPreviewJobForTemplate; no direct Dexie write.
 * Sync Strategy: n/a — polling is inherently online; caller saves via updateDraftImportRun.
 * Conflict Resolution: n/a.
 *
 * Polls the template-mode preview-job endpoint until terminal. Mirror of
 * pollPreviewJob() but tenant-agnostic. Same timeouts and backoff — the
 * worker behind both endpoints is identical.
 */
export async function pollPreviewJobForTemplate(
  jobId: string,
  options: { intervalMs?: number; timeoutMs?: number; onProgress?: (status: PreviewJobStatus) => void } = {},
): Promise<PreviewJobResponse> {
  const { intervalMs = 2_000, timeoutMs = 1_800_000, onProgress } = options;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const job = await getPreviewJobForTemplate(jobId);
    onProgress?.(job.status);

    if (job.status === 'succeeded') return job;
    if (job.status === 'failed') throw new Error(job.errorMessage ?? 'Preview job failed');

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Preview job timed out after waiting too long. Please try again.');
}

// ---------------------------------------------------------------------------
// Create and poll async preview jobs for a specific tenant
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server (online-only exception — creates an async preview job).
 * Write Flow: POST /api/system/import-runs/tenants/:id/preview-jobs → no Dexie write.
 * Sync Strategy: n/a — job creation is inherently online; poll via getPreviewJob.
 * Conflict Resolution: n/a.
 */
export async function createPreviewJob(tenantId: string, params: {
  runId?: string;
  blobUrl: string;
  filename: string;
  fileSize?: number;
  fileType?: string;
  sheetName?: string;
  sampleSize?: number;
}): Promise<{ jobId: string; status: PreviewJobStatus }> {
  return apiFetch(`/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/preview-jobs`, {
    method: 'POST',
    body: JSON.stringify(params),
  }) as Promise<{ jobId: string; status: PreviewJobStatus }>;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — polls an async preview job).
 * Write Flow: GET /api/system/import-runs/tenants/:id/preview-jobs/:id → no Dexie write.
 * Sync Strategy: n/a — polling is inherently online; caller saves via updateDraftImportRun.
 * Conflict Resolution: n/a.
 */
export async function getPreviewJob(tenantId: string, jobId: string): Promise<PreviewJobResponse> {
  return apiFetch(
    `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/preview-jobs/${encodeURIComponent(jobId)}`,
    {},
  ) as Promise<PreviewJobResponse>;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — polls an async preview job).
 * Write Flow: polls API via getPreviewJob; no direct Dexie write.
 * Sync Strategy: n/a — polling is inherently online; caller saves via updateDraftImportRun.
 * Conflict Resolution: n/a.
 *
 * Polls /preview-jobs/:id until the job reaches a terminal status.
 * Calls onProgress on each poll. Rejects if the job fails.
 */
export async function pollPreviewJob(
  tenantId: string,
  jobId: string,
  options: { intervalMs?: number; timeoutMs?: number; onProgress?: (job: PreviewJobResponse) => void } = {},
): Promise<PreviewJobResponse> {
  // 30-minute default — large shapefile ZIPs / FGDBs (100k+ parcels, 100+ MB
  // DBFs) legitimately take several minutes to download + parse. Matches
  // pollCommitRun's timeout. Callers can still override via `timeoutMs`.
  const { intervalMs = 2_000, timeoutMs = 1_800_000, onProgress } = options;
  const deadline = Date.now() + timeoutMs;
  const STALE_MS = 300_000;
  let lastUpdatedAt: string | undefined;
  let lastUpdatedAtSeen = Date.now();

  while (Date.now() < deadline) {
    const job = await getPreviewJob(tenantId, jobId);
    onProgress?.(job);

    if (job.status === 'succeeded') return job;
    if (job.status === 'failed') throw new Error(job.errorMessage ?? 'Preview job failed');

    if (job.updatedAt !== lastUpdatedAt) {
      lastUpdatedAt = job.updatedAt;
      lastUpdatedAtSeen = Date.now();
    } else if (Date.now() - lastUpdatedAtSeen > STALE_MS) {
      throw new Error('Preview processing was interrupted (worker may have restarted). Please try again.');
    }

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Preview job timed out after waiting too long. Please try again.');
}

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` + server (online polling).
 * Write Flow: reads resumable run from Dexie; polls API; updates draft via
 *   updateDraftImportRun (put to Dexie + wakeLocalWrite).
 * Sync Strategy: pull-based via polling; draft state written to Dexie.
 * Conflict Resolution: server wins — poll result overwrites local draft.
 */
export async function resumePreviewJob(
  tenantId: string,
  options: { intervalMs?: number; timeoutMs?: number; onProgress?: (job: PreviewJobResponse) => void } = {},
): Promise<PreviewJobResponse | null> {
  const db = await getSystemDb();
  const runs = (await db.import_runs.toArray()) as ImportRun[];
  const resumable = runs
    .filter(run => !(run as any)._deleted)
    .filter(run => run.tenantId === tenantId)
    .filter(run => run.status === 'parsing')
    .filter(run => !!run.previewJobId)
    .sort((a, b) => (b.updatedAt ?? b.startedAt ?? '').localeCompare(a.updatedAt ?? a.startedAt ?? ''))[0];

  if (!resumable?.previewJobId) return null;

  const job = await pollPreviewJob(tenantId, resumable.previewJobId, options);
  await updateDraftImportRun(resumable._id, {
    status: job.status === 'succeeded' ? 'parsed' : 'parsing',
    parseResult: job.result
      ? {
          fileType: job.result.fileType,
          columns: job.result.columns,
          sampleRows: job.result.sampleRows,
          totalRows: job.result.totalRows,
          hasGeometry: job.result.hasGeometry,
          geometryColumn: job.result.geometryColumn,
          availableSheets: job.result.availableSheets,
          activeSheet: job.result.activeSheet,
        }
      : undefined,
    selectedSheet: job.result?.activeSheet ?? resumable.selectedSheet ?? null,
    fileType: job.result?.fileType ?? resumable.fileType,
    columnCount: job.result?.columns.length ?? resumable.columnCount,
    errorMessage: null,
  });
  return job;
}
/**
 * @localFirst
 * Source of Truth: server (online-only exception — real-time progress polling).
 * Write Flow: polls API; writes terminal run to Dexie + wakeLocalWrite on completion.
 * Sync Strategy: pull-based via explicit polling; terminal state written to Dexie.
 * Conflict Resolution: server wins.
 *
 * After POSTing a dry-run, poll until status reaches a terminal or
 * committable state (awaiting-commit | failed | completed).
 * Hits the API directly (bypasses Dexie) so each poll sees the live state.
 */
export async function pollDryRun(
  tenantId: string,
  runId: string,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    /** Called with the live run snapshot on every poll — surface live progress */
    onProgress?: (run: ImportRun) => void;
  } = {},
): Promise<ImportRun> {
  // 750 ms default matches the backend's 500 ms heartbeat cadence so every
  // poll observes a fresh summary write — see processImportRun.ts.
  const { intervalMs = 750, timeoutMs = 600_000, onProgress } = options;
  const deadline = Date.now() + timeoutMs;
  // Five-minute stale threshold: if the server stops updating a dry run for
  // this long, we assume the run was interrupted and surface a retryable error.
  // This keeps the polling contract aligned with the rest of the UI and the
  // existing unit tests, which intentionally advance time just beyond 5 min.
  const STALE_MS = 300_000;
  const MATERIALIZE_GRACE_MS = 90_000;
  let lastUpdatedAt: string | undefined;
  let lastUpdatedAtSeen = Date.now();

  while (Date.now() < deadline) {
    let run: ImportRun;
    try {
      run = await apiFetch(
        `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(runId)}`,
        {},
      ) as ImportRun;
    } catch (err) {
      if (Date.now() - lastUpdatedAtSeen > MATERIALIZE_GRACE_MS) throw err;
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      continue;
    }
    onProgress?.(run);
    if (run.status === 'awaiting-commit' || run.status === 'failed' || run.status === 'completed' || run.status === 'cancelled') {
      const db = await getSystemDb();
      await db.import_runs.put(run as any).catch(() => { /* Dexie write failure non-fatal — sync loop will retry */ });
      // G-015: wake sync push loop after write-through.
      wakeLocalWrite('import_runs');
      return run;
    }
    // Stale-run detection: if updatedAt hasn't changed for STALE_MS, the server
    // was restarted mid-processing and the run will never reach a terminal state.
    const runUpdatedAt = (run as any).updatedAt as string | undefined;
    if (runUpdatedAt !== lastUpdatedAt) {
      lastUpdatedAt = runUpdatedAt;
      lastUpdatedAtSeen = Date.now();
    } else if (Date.now() - lastUpdatedAtSeen > STALE_MS) {
      throw new Error('Dry run was interrupted (server may have restarted). Please try again.');
    }
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Dry run timed out waiting for results. Please try again.');
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — real-time progress polling).
 * Write Flow: polls API; writes terminal run to Dexie + wakeLocalWrite on completion.
 * Sync Strategy: pull-based via explicit polling; terminal state written to Dexie.
 * Conflict Resolution: server wins.
 *
 * Poll an in-flight commit until the run reaches a terminal state
 * (`completed` | `failed` | `cancelled`). Writes the final run through to
 * Dexie so the wizard / history views see it immediately.
 *
 * Bug #4 fix: the previous wizard flow set `commitStatus:'completed'` the
 * moment the commit POST returned, even though the commit runs as a
 * fire-and-forget background job. This caused silent mis-reporting when
 * a commit actually failed. This helper mirrors `pollDryRun` (same stale
 * detection, same deadline) and is the only correct way to observe a
 * commit completion.
 */
export async function pollCommitRun(
  tenantId: string,
  runId: string,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    onProgress?: (run: ImportRun) => void;
  } = {},
): Promise<ImportRun> {
  // 750 ms default matches the backend's 500 ms heartbeat cadence so every
  // poll observes a fresh summary write — see processImportRun.ts. 30-minute
  // overall deadline for commits which can take materially longer than dry runs.
  const { intervalMs = 750, timeoutMs = 1_800_000, onProgress } = options;
  const deadline = Date.now() + timeoutMs;
  // Commits can take much longer than dry runs for large datasets, so use a
  // 15-minute stale threshold instead of 5 minutes. The overall timeout is
  // still 30 minutes, so this gives the backend ample time to complete.
  const STALE_MS = 900_000;
  let lastUpdatedAt: string | undefined;
  let lastUpdatedAtSeen = Date.now();

  while (Date.now() < deadline) {
    const run = await apiFetch(
      `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(runId)}`,
      {},
    ) as ImportRun;

    onProgress?.(run);

    if (run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled') {
      const db = await getSystemDb();
      await db.import_runs.put(run as any).catch(() => { /* Dexie write failure non-fatal — sync loop will retry */ });
      // G-015: wake sync push loop after write-through.
      wakeLocalWrite('import_runs');
      return run;
    }

    const runUpdatedAt = (run as any).updatedAt as string | undefined;
    if (runUpdatedAt !== lastUpdatedAt) {
      lastUpdatedAt = runUpdatedAt;
      lastUpdatedAtSeen = Date.now();
    } else if (Date.now() - lastUpdatedAtSeen > STALE_MS) {
      throw new Error('Commit was interrupted (server may have restarted). Please try again.');
    }

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Commit timed out waiting for results. Please try again.');
}

// ---------------------------------------------------------------------------
// Create a new import run for a specific tenant
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server (online-only exception — run creation enqueues a worker job).
 * Write Flow: POST /api/system/import-runs/tenants/:id → server; writes run to Dexie
 *   + wakeLocalWrite for immediate history page update.
 * Sync Strategy: pull-based — the created run is synced to Dexie via system sync.
 * Conflict Resolution: server wins.
 */
export async function createImportRun(tenantId: string, params: {
  runId?: string;
  templateId: string;
  filename: string;
  blobUrl?: string;
  isDryRun: boolean;
  importName?: string;
  importDescription?: string;
  importBehavior?: string;
  matchKeyField?: string;
  fileSize?: number;
  fileType?: string;
  columnCount?: number;
  selectedSheet?: string | null;
  // Per-run override for the template's jurisdiction boundary filter.
  // Omit to use the template default.
  boundaryCheckEnabled?: boolean;
  // Phase 5a: required when the template's dataMode is 'parcelOwnerUpdate' —
  // the run's freshness claim stamped to ownership.sourceAsOf.
  dataAsOf?: string;
}): Promise<ImportRun> {
  const run = await apiFetch(`/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'POST',
    body: JSON.stringify(params),
  }) as ImportRun;
  // Write-through to Dexie so history/detail views see the run immediately.
  try {
    const db = await getSystemDb();
    await db.import_runs.put(run as any);
    // G-015: wake sync push loop after write-through.
    wakeLocalWrite('import_runs');
  } catch {
    // best-effort cache update; failures should not break the wizard
  }
  return run;
}

// ---------------------------------------------------------------------------
// List runs for a specific tenant — Dexie only (local-first source of truth)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` (mirrored from server via sync).
 * Write Flow: pure read — no writes.
 * Sync Strategy: pull-based via system sync.
 * Conflict Resolution: server wins — sync overwrites local cache.
 */
export async function listImportRuns(tenantId: string, templateId?: string): Promise<ImportRun[]> {
  const db = await getSystemDb();

  const localDocs = await db.import_runs.toArray();
  const local = (localDocs as ImportRun[])
    .filter(r => !(r as any)._deleted)
    .filter(r => (r as any).tenantId === tenantId)
    .filter(r => !templateId || (r as any).templateId === templateId)
    .sort((a, b) => {
      const aDate = (a as any).startedAt ?? '';
      const bDate = (b as any).startedAt ?? '';
      return bDate.localeCompare(aDate);
    });
  return local;
}

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` (mirrored from server via sync).
 * Write Flow: pure read — no writes (delegates to listImportRuns).
 * Sync Strategy: pull-based via system sync.
 * Conflict Resolution: server wins — sync overwrites local cache.
 */
export async function refreshImportRuns(tenantId: string, templateId?: string): Promise<ImportRun[]> {
  return listImportRuns(tenantId, templateId);
}

// ---------------------------------------------------------------------------
// List all runs across all tenants — Dexie only (local-first source of truth)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` (mirrored from server via sync).
 * Write Flow: pure read — no writes.
 * Sync Strategy: pull-based via system sync.
 * Conflict Resolution: server wins — sync overwrites local cache.
 */
export async function listAllImportRuns(): Promise<ImportRun[]> {
  const db = await getSystemDb();

  const localDocs = await db.import_runs.toArray();
  const local = (localDocs as ImportRun[])
    .filter(r => !(r as any)._deleted)
    .sort((a, b) => {
      const aDate = (a as any).startedAt ?? '';
      const bDate = (b as any).startedAt ?? '';
      return bDate.localeCompare(aDate);
    });
  return local;
}

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` (mirrored from server via sync).
 * Write Flow: pure read — no writes (delegates to listAllImportRuns).
 * Sync Strategy: pull-based via system sync.
 * Conflict Resolution: server wins — sync overwrites local cache.
 */
export async function refreshAllImportRuns(): Promise<ImportRun[]> {
  return listAllImportRuns();
}

// ---------------------------------------------------------------------------
// Get a single run for a specific tenant — Dexie only (local-first source of truth)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` (mirrored from server via sync).
 * Write Flow: pure read — no writes.
 * Sync Strategy: pull-based via system sync.
 * Conflict Resolution: server wins — sync overwrites local cache.
 */
export async function getImportRun(tenantId: string | undefined, id: string): Promise<ImportRun> {
  const db = await getSystemDb();
  const localDoc = await db.import_runs.get(id) as ImportRun | undefined;
  if (!localDoc) {
    throw new Error('Import run not found in local database. Please allow sync to finish and try again.');
  }
  if (tenantId && (localDoc as any).tenantId && (localDoc as any).tenantId !== tenantId) {
    throw new Error('Import run was found under a different department in local data.');
  }
  return localDoc;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — explicit API refresh).
 * Write Flow: GET /api/system/import-runs/... → server; writes run to Dexie
 *   + wakeLocalWrite.
 * Sync Strategy: pull-based via explicit refresh.
 * Conflict Resolution: server wins.
 */
export async function refreshImportRun(tenantId: string | undefined, id: string): Promise<ImportRun> {
  const db = await getSystemDb();
  const requestPath = tenantId
    ? `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(id)}`
    : `/api/system/import-runs/${encodeURIComponent(id)}`;
  const data = await apiFetch(requestPath) as ImportRun;
  await db.import_runs.put(data as any);
  // G-015: wake sync push loop after write-through.
  wakeLocalWrite('import_runs');
  return data;
}

export function isImportRunActive(run: Pick<ImportRun, 'status'> | null | undefined): boolean {
  return !!run && ACTIVE_STATUSES.has(run.status);
}

// ---------------------------------------------------------------------------
// Commit a dry-run for a specific tenant — online-only, write result through to Dexie
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server (online-only exception — commit enqueues a worker job).
 * Write Flow: POST /api/system/import-runs/tenants/:id/:id/commit → server;
 *   background refresh writes the updated run to Dexie + wakeLocalWrite.
 * Sync Strategy: pull-based — committed run is synced to Dexie via background refresh.
 * Conflict Resolution: server wins.
 */
export async function commitImportRun(tenantId: string, id: string): Promise<{ _id: string; status: string }> {
  const result = await apiFetch(`/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(id)}/commit`, {
    method: 'POST',
  }) as { _id: string; status: string };

  apiFetch(`/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(id)}`)
    .then(async (data: ImportRun) => {
      const db = await getSystemDb();
      await db.import_runs.put(data as any);
      // G-015: wake sync push loop after write-through.
      wakeLocalWrite('import_runs');
    })
    .catch(() => { /* ignore */ });

  return result;
}

// ---------------------------------------------------------------------------
// 3.1: column-mapping suggestion proxy. Calls the system-api which forwards
// to tenant-api's /api/import-runs/suggest-mappings (rule engine + AI).
// Returned suggestions carry mappingSource='ai' for LLM-tagged entries
// (2.7) so the wizard can render an AI badge.
// ---------------------------------------------------------------------------
/**
 * @contract MappingSuggestion
 * Purpose: A single column mapping suggestion from the rule engine or AI matcher.
 * Fields: sourceColumn, targetField, transform, confidence, mappingSource?, reasoning?.
 * Rules: mappingSource is 'ai' for LLM-tagged entries so the wizard can render an AI badge.
 * Sync: Returned from the online suggest-mappings endpoint; not persisted directly.
 */
export interface MappingSuggestion {
  sourceColumn: string;
  targetField: string;
  transform: 'none' | 'number' | 'date' | 'trim' | 'uppercase' | 'lowercase' | 'concat' | 'split';
  confidence: number;
  mappingSource?: 'rule' | 'ai';
  reasoning?: string;
}

/**
 * @contract SuggestMappingsResult
 * Purpose: Result of the column mapping suggestion flow (Tier 1 rule + Tier 2 AI).
 * Fields: suggestions[], unmatchedColumns[], tier1Coverage, tier2Used, tier2Error?.
 * Rules: tier2Error is set only if the AI tier was attempted and failed.
 * Sync: Returned from the online suggest-mappings endpoint; not persisted directly.
 */
export interface SuggestMappingsResult {
  suggestions: MappingSuggestion[];
  unmatchedColumns: string[];
  tier1Coverage: number;
  tier2Used: boolean;
  tier2Error?: string;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — AI/rule mapping suggestion).
 * Write Flow: POST /api/system/import-runs/tenants/:id/suggest-mappings → no Dexie write.
 * Sync Strategy: n/a — suggestion is inherently online; result feeds the mapping UI.
 * Conflict Resolution: n/a.
 */
export async function suggestMappings(
  tenantId: string,
  params: {
    dataMode: 'parcel' | 'lookup' | 'contact' | 'generic' | 'parcelOwnerUpdate';
    columns: string[];
    sampleRows?: Record<string, unknown>[];
    tier?: 'rule' | 'ai' | 'both';
  },
): Promise<SuggestMappingsResult> {
  return apiFetch(
    `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/suggest-mappings`,
    {
      method: 'POST',
      body: JSON.stringify({
        dataMode: params.dataMode,
        columns: params.columns,
        sampleRows: params.sampleRows ?? [],
        tier: params.tier ?? 'both',
      }),
    },
  ) as Promise<SuggestMappingsResult>;
}

// Tenant-agnostic variant for the template editor. See parseImportFileForTemplate
// for rationale. Both use the proxy-tenant pattern on system-api.

/**
 * @localFirst
 * Source of Truth: server (online-only exception — AI/rule mapping suggestion, tenant-agnostic).
 * Write Flow: POST /api/system/import-runs/suggest-mappings → no Dexie write.
 * Sync Strategy: n/a — suggestion is inherently online; result feeds the template editor.
 * Conflict Resolution: n/a.
 */
export async function suggestMappingsForTemplate(
  params: {
    dataMode: 'parcel' | 'lookup' | 'contact' | 'generic' | 'parcelOwnerUpdate';
    columns: string[];
    sampleRows?: Record<string, unknown>[];
    tier?: 'rule' | 'ai' | 'both';
  },
): Promise<SuggestMappingsResult> {
  return apiFetch(
    `/api/system/import-runs/suggest-mappings`,
    {
      method: 'POST',
      body: JSON.stringify({
        dataMode: params.dataMode,
        columns: params.columns,
        sampleRows: params.sampleRows ?? [],
        tier: params.tier ?? 'both',
      }),
    },
  ) as Promise<SuggestMappingsResult>;
}

// ---------------------------------------------------------------------------
// Phase 5.6: Enrichment run — create a run that updates selected fields on
// existing parcels using AI transforms. No source file needed.
// Proxy to tenant-api /api/import-runs/enrichment via system-api.
// ---------------------------------------------------------------------------

/**
 * @contract CreateEnrichmentRunResult
 * Purpose: Result of creating an AI enrichment run that updates selected fields on
 *   existing parcels without a source file.
 * Fields: runId, status, estimatedCandidates.
 * Rules: estimatedCandidates is advisory; the actual processed count may differ.
 * Sync: Returned from the online enrichment endpoint; run synced to Dexie via system sync.
 */
export interface CreateEnrichmentRunResult {
  runId: string;
  status: string;
  estimatedCandidates: number;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — enrichment run enqueues a worker job).
 * Write Flow: POST /api/system/import-runs/tenants/:id/enrichment → no Dexie write.
 * Sync Strategy: pull-based — the created run is synced to Dexie via system sync.
 * Conflict Resolution: server wins.
 */
export async function createEnrichmentRun(
  tenantId: string,
  params: {
    templateId: string;
    targetFields: string[];
    jurisdictionFilter?: string;
    sourceRunId?: string;
    isDryRun?: boolean;
    importName?: string;
    importDescription?: string;
  },
): Promise<CreateEnrichmentRunResult> {
  return apiFetch(
    `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/enrichment`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
  ) as Promise<CreateEnrichmentRunResult>;
}

// ---------------------------------------------------------------------------
// Column group interpretation (Phase 5.4) — AI-assisted grouping of related
// source columns (e.g. address fields, owner name fields) into logical units
// with suggested mappings and transforms.
// ---------------------------------------------------------------------------

/**
 * @contract GroupInterpretation
 * Purpose: AI-assisted grouping of related source columns into a logical unit
 *   with suggested mappings and transforms.
 * Fields: groupColumns[], groupType, mappings[], confidence, reasoning,
 *   suggestedTransforms?.
 * Rules: mappings are advisory; the author confirms before applying.
 * Sync: Returned from the online interpret-column-groups endpoint; not persisted.
 */
export interface GroupInterpretation {
  groupColumns: string[];
  groupType: string;
  mappings: Array<{
    sourceColumn: string;
    targetField: string;
    confidence: number;
  }>;
  confidence: number;
  reasoning: string;
  suggestedTransforms?: Array<{
    type: 'concatenate' | 'split' | 'parse' | 'classify';
    sourceColumns: string[];
    targetField: string;
    instruction?: string;
  }>;
}

/**
 * @contract InterpretColumnGroupsResult
 * Purpose: Result of the AI column group interpretation flow.
 * Fields: interpretations[], used (whether AI was used), error?.
 * Rules: error is set only if the AI tier was attempted and failed.
 * Sync: Returned from the online interpret-column-groups endpoint; not persisted.
 */
export interface InterpretColumnGroupsResult {
  interpretations: GroupInterpretation[];
  used: boolean;
  error?: string;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — AI column group interpretation).
 * Write Flow: POST /api/system/import-runs/tenants/:id/interpret-column-groups → no Dexie write.
 * Sync Strategy: n/a — interpretation is inherently online; result feeds the mapping UI.
 * Conflict Resolution: n/a.
 */
export async function interpretColumnGroups(
  tenantId: string,
  params: {
    dataMode: 'parcel' | 'lookup' | 'contact' | 'generic' | 'parcelOwnerUpdate';
    columns: string[];
    sampleRows?: Record<string, unknown>[];
    groups?: Array<{ columns: string[]; groupHint?: string }>;
    autoDetect?: boolean;
  },
): Promise<InterpretColumnGroupsResult> {
  return apiFetch(
    `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/interpret-column-groups`,
    {
      method: 'POST',
      body: JSON.stringify({
        dataMode: params.dataMode,
        columns: params.columns,
        sampleRows: params.sampleRows ?? [],
        groups: params.groups,
        autoDetect: params.autoDetect ?? true,
      }),
    },
  ) as Promise<InterpretColumnGroupsResult>;
}

// ---------------------------------------------------------------------------
// Row-level classified results — Apr 2026 Dry Run Summary redesign.
// ---------------------------------------------------------------------------
// Documented local-first bypass: rows are NEVER cached in Dexie. Up to 1M
// rows per run would exceed IndexedDB practical limits and each search
// query is unique, so caching has zero hit rate for the end user.
// See plans/dry-run-summary-redesign-50004a.md §2.
// ---------------------------------------------------------------------------

/**
 * @contract ImportRunRowCategory
 * Purpose: Closed enum of classified row categories for the dry-run summary redesign.
 * Fields: 'all' | 'imported' | 'imported.create' | 'imported.update' | 'excluded' |
 *   'excluded.outOfJurisdiction' | 'excluded.missingCoordinates' |
 *   'excluded.duplicateInsertOnly' | 'excluded.missingMatchKey' | 'failed' |
 *   'failed.transform' | 'failed.entityBuild' | 'removed'.
 * Rules: Categories map 1:1 to ImportRunSummary counters.
 * Sync: Used as a query filter for the online /rows endpoint; not persisted.
 */
export type ImportRunRowCategory =
  | 'all'
  | 'imported'
  | 'imported.create'
  | 'imported.update'
  | 'excluded'
  | 'excluded.outOfJurisdiction'
  | 'excluded.missingCoordinates'
  | 'excluded.duplicateInsertOnly'
  | 'excluded.missingMatchKey'
  | 'failed'
  | 'failed.transform'
  | 'failed.entityBuild'
  | 'removed';

/**
 * @contract ImportRunRow
 * Purpose: A single classified row from the dry-run/commit row evidence endpoint.
 * Fields: rowNumber, matchKeyValue, status, reason?, coordinates?,
 *   fieldTransforms? (per-field transform provenance incl. AI outputs).
 * Rules: status maps to ImportRunRowCategory values.
 * Sync: Returned from the online /rows endpoint; NEVER cached in Dexie (up to 1M rows).
 */
export interface ImportRunRow {
  rowNumber: number;
  matchKeyValue: string;
  status: string;
  reason?: string;
  coordinates?: [number, number] | null;
  fieldTransforms?: FieldTransformEvidence[];
}

/**
 * @contract ImportRunRowsPage
 * Purpose: A paginated response of classified rows for a terminal import run.
 * Fields: runId, category (ImportRunRowCategory), total, page, pageSize, rows[].
 * Rules: page is 0-based; pageSize is one of 25/50/100/250.
 * Sync: Returned from the online /rows endpoint; NEVER cached in Dexie.
 */
export interface ImportRunRowsPage {
  runId: string;
  category: ImportRunRowCategory;
  total: number;
  page: number;
  pageSize: number;
  rows: ImportRunRow[];
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — row evidence is NEVER cached in Dexie).
 * Write Flow: GET /api/system/import-runs/tenants/:id/:id/rows → no Dexie write.
 * Sync Strategy: n/a — rows are never cached (up to 1M rows would exceed IndexedDB limits).
 * Conflict Resolution: n/a.
 *
 * Fetch a page of classified rows for a terminal import run.
 *
 * @param tenantId — run's tenant (from the wizard state).
 * @param runId — run ObjectId hex.
 * @param params — paging + filter parameters. Signal is forwarded so the
 *   caller can cancel mid-flight when filters change.
 */
export async function listImportRunRows(
  tenantId: string,
  runId: string,
  params: {
    category?: ImportRunRowCategory;
    page?: number;
    pageSize?: 25 | 50 | 100 | 250;
    q?: string;
    sort?: 'rowNumber:asc' | 'rowNumber:desc';
    signal?: AbortSignal;
  } = {},
): Promise<ImportRunRowsPage> {
  const qs = new URLSearchParams();
  qs.set('category', params.category ?? 'all');
  qs.set('page', String(params.page ?? 0));
  qs.set('pageSize', String(params.pageSize ?? 50));
  if (params.q) qs.set('q', params.q);
  qs.set('sort', params.sort ?? 'rowNumber:asc');
  return apiFetch(
    `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(runId)}/rows?${qs.toString()}`,
    { signal: params.signal },
  ) as Promise<ImportRunRowsPage>;
}

/**
 * @localFirst
 * Source of Truth: server (online-only exception — CSV export download).
 * Write Flow: GET /api/system/import-runs/tenants/:id/:id/rows.csv → Blob download.
 * Sync Strategy: n/a — export is inherently online; no Dexie write.
 * Conflict Resolution: n/a.
 *
 * Trigger a CSV download for the filtered row set. Uses fetch() + Blob so
 * the Authorization header and cookies are carried through apiFetch's layer
 * (an <a download> would lose those in SPA auth).
 */
export async function downloadImportRunRowsCsv(
  tenantId: string,
  runId: string,
  params: { category: ImportRunRowCategory; q?: string },
): Promise<void> {
  const qs = new URLSearchParams();
  qs.set('category', params.category);
  if (params.q) qs.set('q', params.q);
  const url = `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(runId)}/rows.csv?${qs.toString()}`;
  const token = await getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers,
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    throw new Error(message || `CSV export failed (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    const filenameHeader = res.headers.get('content-disposition') ?? '';
    const match = /filename="?([^";]+)"?/.exec(filenameHeader);
    a.download = match?.[1] ?? `import-run-${runId}-${params.category}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

// ---------------------------------------------------------------------------
// Resolve the source-file download URL for an import run.
//
// 1.9a.5 — "View what was imported" UX. Prefers the new shared-attachments
// ref (kind='importSource') and fetches a short-lived read SAS URL via the
// shared `/api/attachments/url/:blobName` endpoint. Falls back to the
// legacy `sourceFileBlobUrl` / `blobUrl` fields for runs created before
// the cut-over so users can still see what was imported.
//
// Returns null when the run has no usable source reference.
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: Dexie `import_runs` + server (API fallback for uncached runs).
 * Write Flow: reads run from Dexie; falls back to API fetch + Dexie put +
 *   wakeLocalWrite if not cached; fetches SAS URL from /api/attachments/url.
 * Sync Strategy: pull-based via API fallback for uncached runs.
 * Conflict Resolution: server wins.
 */
export async function getImportSourceDownloadUrl(
  tenantId: string,
  runId: string,
): Promise<{ url: string; source: 'attachment' | 'legacy'; fileName?: string } | null> {
  // Read the run — Dexie first (fast), API as fallback for runs the user
  // hasn't seen yet on this device.
  const db = await getSystemDb();
  let run = await db.import_runs.get(runId) as any;
  if (!run) {
    try {
      run = await apiFetch(
        `/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(runId)}`,
        {},
      );
      if (run) {
        await db.import_runs.put(run as any).catch(() => { /* Dexie write failure non-fatal — sync loop will retry */ });
        // G-015: wake sync push loop after write-through.
        wakeLocalWrite('import_runs');
      }
    } catch {
      run = null;
    }
  }
  if (!run) return null;

  // Prefer the new shared attachment ref.
  const attachment = Array.isArray(run.attachments)
    ? run.attachments.find((a: any) => a && a.kind === 'importSource' && !a._deleted)
    : null;
  if (attachment?.blobName) {
    try {
      const encoded = (attachment.blobName as string)
        .split('/')
        .map(encodeURIComponent)
        .join('/');
      const result = await apiFetch(`/api/attachments/url/${encoded}`, {}) as { url?: string };
      if (result?.url) {
        return { url: result.url, source: 'attachment', fileName: attachment.fileName as string };
      }
    } catch (err) {
      console.warn('[systemImportRunService] Could not fetch attachment SAS URL:', err);
    }
  }

  // Fall back to legacy fields. These usually lack SAS so the browser
  // download will only work for tenants whose legacy container was public
  // or whose URLs were stored with SAS embedded — best-effort only.
  const legacy = run.sourceFileBlobUrl ?? run.blobUrl;
  if (typeof legacy === 'string' && legacy.length > 0) {
    return { url: legacy, source: 'legacy', fileName: run.filename as string | undefined };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Cancel a run for a specific tenant — online-only, write terminal state through to Dexie
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: server (online-only exception — cancel enqueues a worker action).
 * Write Flow: POST /api/system/import-runs/tenants/:id/:id/cancel → server; patches
 *   local Dexie status to 'cancelled' + wakeLocalWrite for immediate UI feedback.
 * Sync Strategy: pull-based — cancelled run is synced to Dexie via system sync.
 * Conflict Resolution: server wins.
 */
export async function cancelImportRun(tenantId: string, id: string): Promise<{ _id: string; status: string }> {
  const result = await apiFetch(`/api/system/import-runs/tenants/${encodeURIComponent(tenantId)}/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
  }) as { _id: string; status: string };

  const db = await getSystemDb();
  const existing = await db.import_runs.get(id);
  if (existing) {
    await db.import_runs.put({ ...(existing as any), status: 'cancelled' });
    // G-015: wake sync push loop after write-through.
    wakeLocalWrite('import_runs');
  }

  return result;
}